import { ErrorSidePanel } from '@/components/errors';
import { type SecurityCode } from '@/portal-api.definition';
import {
  ModuleName,
  Screen,
  type SkinPageConfig,
  templates,
  utils,
} from '@nosinovacao/nosid-mfe-common';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import z from 'zod';
import { kidProfileSchema } from '../-model/kid-profile.schema';
import { profileFormLoaderFn } from '../-model/profile-form';
import { useGetAvatars } from './-hooks/avatars.hooks';
import { type AvatarProps } from './-model/props';
import { AvatarsLight, AvatarsWooDark, AvatarsWoo } from './-skins';

export const Route = createFileRoute('/(tv)/tv/$id/(tv-profiles)/_sheet/profiles/select-avatar/')({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
  loader: profileFormLoaderFn,
  staticData: {
    module: ModuleName.Tv,
    screen: Screen.SelectAvatar,
  },
  validateSearch: kidProfileSchema.extend({
    profileId: z
      .preprocess((val) => (typeof val === 'string' ? val.replace(/^"|"$/g, '') : val), z.string())
      .optional(),
    hasPin: z.preprocess((val) => val === 'true' || val === true, z.boolean()).default(false),
  }),
});

function RouteComponent() {
  const data = Route.useLoaderData();
  const { kids, profileId, hasPin } = Route.useSearch();
  const { id } = Route.useParams();
  const location = useLocation();
  const validationState = location.state as
    | { pin?: string; securityCode?: SecurityCode }
    | undefined;
  const { data: avatars, isLoading } = useGetAvatars(
    templates.isWooTemplate ? 'Woo' : kids ? 'Kids' : 'Preset',
  );

  const [links, setLinks] = useState<AvatarProps['links']>({});
  useEffect(() => {
    async function mapLinks() {
      const newList: AvatarProps['links'] = {};
      await Promise.all(
        avatars?.map(async (a) => {
          const payload = { ...data, avatar: a };
          const compressed = await utils.compressToBase64Url(JSON.stringify(payload));
          newList[a.Id] = compressed;
        }) ?? [],
      );
      setLinks(newList);
    }
    mapLinks();
  }, [avatars, data]);
  const props: AvatarProps = {
    list: avatars ?? [],
    isLoading,
    links,
    kids,
    sa: id,
    profileId,
    hasPin,
    validationState,
  };
  return templates.getSkinSelector(getSkin(props));
}

function getSkin(props: AvatarProps): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <AvatarsWooDark {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <AvatarsWoo {...props} />,
      },
    ],
    defaultValue: <AvatarsLight {...props} />,
  };
}
